import React, { useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { GALLERY_SOURCES } from "@/data/content";
import { lockPageScroll, unlockPageScroll } from "@/lib/scrollLock";
import { useAdminContent } from "@/lib/adminContent";

/** Order matches current layout: top mosaic (4), then two rows × 2 tiles — never 3 across on mobile. */
const SOURCES = GALLERY_SOURCES;
const RESPONSIVE_GALLERY_SOURCES = new Set(GALLERY_SOURCES);
const GALLERY_LIGHTBOX_LOCK_ID = "gallery-lightbox";
const toSrcSet = (src) => {
  if (!RESPONSIVE_GALLERY_SOURCES.has(src)) return undefined;
  const extIdx = src.lastIndexOf(".");
  if (extIdx === -1) return undefined;
  const base = src.slice(0, extIdx);
  return `${base}-xs.webp 320w, ${base}-sm.webp 560w, ${base}-md.webp 820w, ${base}.webp 1024w`;
};

const Gallery = () => {
  const { t, dict, lang } = useI18n();
  const { gallery: adminGallery } = useAdminContent(lang);
  const labels = adminGallery.length ? adminGallery.map((x) => x.title) : dict.gallery.images;
  const sources = adminGallery.length ? adminGallery.map((x) => x.image) : SOURCES;
  const [active, setActive] = useState(null);
  const lightboxRoot = useMemo(
    () => (typeof document !== "undefined" ? document.body : null),
    []
  );

  useLayoutEffect(() => {
    if (active !== null) lockPageScroll(GALLERY_LIGHTBOX_LOCK_ID);
    else unlockPageScroll(GALLERY_LIGHTBOX_LOCK_ID);
    return () => unlockPageScroll(GALLERY_LIGHTBOX_LOCK_ID);
  }, [active]);

  return (
    <section id="gallery" className="section" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="mb-8 sm:mb-10 flex items-end justify-between flex-wrap gap-4"
        >
          <div>
            <div className="eyebrow">{t("gallery.eyebrow")}</div>
            <h2 className="font-display text-3xl sm:text-5xl md:text-6xl font-bold tracking-tight mt-3 max-w-3xl leading-[1.05]" data-testid="gallery-title">
              {t("gallery.title1")}{" "}
              <span className="accent-text italic font-light">{t("gallery.title2")}</span>
            </h2>
          </div>
          <div className="text-xs sm:text-sm text-[var(--text-mute)] max-w-xs">
            {t("gallery.desc")}
          </div>
        </motion.div>

        <div className="grid grid-cols-12 gap-3 sm:gap-4">
          {sources.map((src, i) => {
            const size =
              i === 0 ? "col-span-12 md:col-span-7 row-span-2 h-[260px] sm:h-[400px] md:h-[480px]"
              : i === 1 ? "col-span-12 md:col-span-5 h-[180px] sm:h-[232px]"
              : i === 2 ? "col-span-6 md:col-span-3 h-[180px] sm:h-[232px]"
              : i === 3 ? "col-span-6 md:col-span-2 h-[180px] sm:h-[232px]"
              : "col-span-6 md:col-span-6 h-[200px] sm:h-[232px] md:h-[240px]";

            // Mobile layout: first 2 tiles are full-width, the rest are 50% width.
            // This prevents Lighthouse from selecting the -md candidate for small containers.
            const mobileSizes = i < 2 ? "100vw" : "50vw";
            const sizes = `(max-width: 639px) ${mobileSizes}, (max-width: 767px) 50vw, 40vw`;

            return (
              <motion.button
                key={`gallery-${i}-${src}`}
                onClick={() => setActive(i)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                className={`${size} relative overflow-hidden rounded-2xl sm:rounded-3xl group glass-strong p-0 cursor-pointer text-left`}
                data-testid={`gallery-tile-${i}`}
              >
                <img
                  src={src}
                  srcSet={toSrcSet(src)}
                    sizes={sizes}
                  alt={labels[i]}
                  width="1024"
                  height="682"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#07070b]/95 via-[#07070b]/30 to-transparent" />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/5 rounded-2xl sm:rounded-3xl" />
                <div className="absolute bottom-3 sm:bottom-5 left-3 sm:left-5 right-3 sm:right-5 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
                      {String(i + 1).padStart(2, "0")} / {String(sources.length).padStart(2, "0")}
                    </div>
                    <div className="mt-1 font-display text-base sm:text-xl font-semibold text-white">{labels[i]}</div>
                  </div>
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full glass flex items-center justify-center group-hover:bg-[var(--accent)] group-hover:border-[var(--accent)] transition-colors">
                    <ChevronRight size={15} />
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {lightboxRoot && createPortal(
        <AnimatePresence>
          {active !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mobile-performance-surface fixed inset-0 z-[999] bg-black sm:bg-black/92 sm:backdrop-blur-md flex items-center justify-center p-4"
            style={{
              paddingTop: "calc(env(safe-area-inset-top, 0px) + 72px)",
              paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)",
            }}
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
            data-overlay-root="true"
            data-overlay-name={GALLERY_LIGHTBOX_LOCK_ID}
          >
            <button
              onClick={(e) => { e.stopPropagation(); setActive((a) => (a - 1 + sources.length) % sources.length); }}
              className="absolute left-3 sm:left-8 z-[2] w-11 h-11 sm:w-12 sm:h-12 rounded-full glass flex items-center justify-center hover:bg-white/10"
              aria-label={t("ui.prev")}
              data-testid="gallery-prev"
            >
              <ChevronLeft />
            </button>
            <motion.img
              key={active}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              src={sources[active]}
              alt={labels[active]}
              width="1024"
              height="682"
              decoding="async"
              className="relative z-[1] max-w-[92vw] max-h-[80vh] rounded-2xl object-contain"
              data-scroll-lock-allow="true"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => { e.stopPropagation(); setActive((a) => (a + 1) % sources.length); }}
              className="absolute right-3 sm:right-8 z-[2] w-11 h-11 sm:w-12 sm:h-12 rounded-full glass flex items-center justify-center hover:bg-white/10"
              aria-label={t("ui.next")}
              data-testid="gallery-next"
            >
              <ChevronRight />
            </button>
            <button
              onClick={() => setActive(null)}
              className="absolute right-3 z-[2] w-11 h-11 sm:w-12 sm:h-12 rounded-full glass flex items-center justify-center hover:bg-white/10"
              style={{ top: "calc(env(safe-area-inset-top, 0px) + 56px)" }}
              aria-label={t("ui.close")}
              data-testid="gallery-close"
            >
              <X />
            </button>
          </motion.div>
          )}
        </AnimatePresence>,
        lightboxRoot
      )}
    </section>
  );
};

export default Gallery;
